import React, { useState, useEffect, useRef } from "react";
import Papa from "papaparse";

// --- IndexedDB/Web Worker Setup (INDEPENDENT) ---
const WORKER_SCRIPT = `
  let db;
  function normalize(str) {
    // Ensure input is a string before processing
    return String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace(/\\s+/g, "").trim();
  }
  function levenshtein(a, b) {
    // Ensure inputs are strings before normalizing
    const strA = String(a || "");
    const strB = String(b || "");
    if (!strA || !strB) return Math.max(strA.length, strB.length);
    const matrix = Array.from({ length: strB.length + 1 }, (_, i) =>
      Array(strA.length + 1).fill(0)
    );
    for (let i = 0; i <= strA.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= strB.length; j++) matrix[j][0] = j;
    for (let j = 1; j <= strB.length; j++) {
      for (let i = 1; i <= strA.length; i++) {
        const cost = strA[i - 1] === strB[j - 1] ? 0 : 1; // Cost is 1 if different, 0 if same
        matrix[j][i] = Math.min(
          matrix[j - 1][i - 1] + cost, // Substitution/Match
          matrix[j][i - 1] + 1,       // Deletion
          matrix[j - 1][i] + 1        // Insertion
        );
      }
    }
    return matrix[strB.length][strA.length];
  }
  function fuzzyMatch(a, b, maxDistance = 2) {
    // Ensure inputs are strings before normalizing
    const strA = String(a || "");
    const strB = String(b || "");
    return levenshtein(normalize(strA), normalize(strB)) <= maxDistance;
  }
  function openDB() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open("mergeToolDB", 1);
      req.onupgradeneeded = function (e) {
        db = e.target.result;
        if (!db.objectStoreNames.contains("json")) db.createObjectStore("json", { keyPath: "id" });
        if (!db.objectStoreNames.contains("csv")) db.createObjectStore("csv", { keyPath: "id" });
      };
      req.onsuccess = function (e) { db = e.target.result; resolve(); };
      req.onerror = function (e) { reject(e); };
    });
  }
  function clearStores() {
    return new Promise((resolve) => {
      if (!db) return resolve();
      const tx = db.transaction(["json", "csv"], "readwrite");
      tx.objectStore("json").clear();
      tx.objectStore("csv").clear();
      tx.oncomplete = resolve;
    });
  }
  function storeArray(storeName, arr) {
    return new Promise((resolve) => {
      const tx = db.transaction([storeName], "readwrite");
      const store = tx.objectStore(storeName);
      arr.forEach((item, idx) => store.put({ ...item, id: idx }));
      tx.oncomplete = resolve;
    });
  }
  function getAll(storeName) {
    return new Promise((resolve) => {
      const tx = db.transaction([storeName], "readonly");
      const store = tx.objectStore(storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve([]);
    });
  }
  function comparePractices(jsonPractice, csvPractice) {
    const fields = [
      { json: "prac_name", csv: "Name", label: "Practice Name" },
      { json: "address1", csv: "Address 1", label: "Address 1" },
      { json: "address2", csv: "Address 2", label: "Address 2" },
      { json: "address3", csv: "Address 3", label: "Address 3" },
      { json: "address4", csv: "Town/City", label: "Address 4 (Town/City)" },
      { json: "postcode", csv: "Postcode", label: "Postcode" }
    ];
    const diffs = [];
    const addressFieldsToIgnoreNA = ["address2", "address3", "address4"]; // JSON fields where NA vs empty is ignored

    fields.forEach(f => {
      const oldValue = jsonPractice[f.json] || "";
      const newValue = csvPractice[f.csv] || "";

      // --- Refined Difference Check ---
      let ignoreDifference = false;

      // Condition 1: Ignore if new value is empty/null/undefined but old value is not
      const isNewValueEffectivelyEmpty = newValue === null || newValue === undefined || String(newValue).trim() === "";
      const isOldValueEffectivelyEmpty = oldValue === null || oldValue === undefined || String(oldValue).trim() === "";

      if (isNewValueEffectivelyEmpty && !isOldValueEffectivelyEmpty) {
        // Don't show a difference if CSV is empty but JSON has data
        ignoreDifference = true;
        // console.log(\`[Compare Ignore] Field: \${f.label}, CSV empty, JSON has value '\${oldValue}'.\`); // Optional debug log
      }

      // Condition 2: Ignore 'na' vs empty for specific address fields (only if not already ignored)
      if (!ignoreDifference && addressFieldsToIgnoreNA.includes(f.json)) {
        const oldNorm = normalize(oldValue);
        const newNorm = normalize(newValue);
        // Check if one is 'na' and the other is empty
        if ((oldNorm === 'na' && newNorm === '') || (oldNorm === '' && newNorm === 'na')) {
          ignoreDifference = true;
          // console.log(\`[Compare Ignore] Field: \${f.label}, 'na' vs empty.\`); // Optional debug log
        }
      }

      // Only add difference if not ignored and normalized values *still* differ
      // (This handles cases where JSON is empty and CSV has data, or both have different non-empty data)
      if (!ignoreDifference && normalize(oldValue) !== normalize(newValue)) {
        diffs.push({
          field: f.label,
          oldValue,
          newValue
        });
      }
      // --- End Refined Difference Check ---
    });
    return diffs;
  }
  function generatePracCode(existingCodes) {
    let maxNum = 0;
    existingCodes.forEach(code => {
      if (typeof code === "string" && code.startsWith("N")) {
        const num = parseInt(code.slice(1), 10);
        if (!isNaN(num) && num > maxNum) maxNum = num;
      }
    });
    return "N" + String(maxNum + 1).padStart(5, "0");
  }
  self.onmessage = async function (e) {
    const { action, data } = e.data;
    console.log('[Worker] Received action:', action); // Log received action

    try { // Add top-level try...catch
      if (action === "init") {
        await openDB();
        await clearStores(); // Clears DB on init - this is why refresh requires re-upload
        console.log('[Worker] DB initialized and cleared.');
        self.postMessage({ action: "init", status: "ok" });
      }
      if (action === "storeJSON") {
        await storeArray("json", data);
        console.log('[Worker] Stored JSON data count:', data.length);
        self.postMessage({ action: "storeJSON", status: "ok" });
      }
      if (action === "storeCSV") {
        await storeArray("csv", data);
        console.log('[Worker] Stored CSV data count:', data.length);
        self.postMessage({ action: "storeCSV", status: "ok" });
      }
      if (action === "compare") {
        console.log('[Worker] Starting comparison...');
        const jsonArr = await getAll("json");
        const csvArr = await getAll("csv");
        console.log('[Worker] Fetched data for comparison:', { jsonCount: jsonArr.length, csvCount: csvArr.length });

        if (jsonArr.length === 0 || csvArr.length === 0) {
           console.warn('[Worker] Cannot compare, one or both datasets are empty.');
           self.postMessage({ action: "compareResult", matches: [], definiteNew: [], potentialDuplicates: [], possibleNew: [] });
           return;
        }

        // --- Optimization: Index JSON data by normalized postcode ---
        console.time('[Worker] Indexing JSON data');
        const jsonIndexByPostcode = new Map();
        const jsonIndexByName = new Map(); // Index by normalized name as well
        jsonArr.forEach(jp => {
          const normalizedPostcode = normalize(jp.postcode);
          if (!jsonIndexByPostcode.has(normalizedPostcode)) {
            jsonIndexByPostcode.set(normalizedPostcode, []);
          }
          jsonIndexByPostcode.get(normalizedPostcode).push(jp);

          const normalizedName = normalize(jp.prac_name);
           if (!jsonIndexByName.has(normalizedName)) {
            jsonIndexByName.set(normalizedName, []);
          }
          jsonIndexByName.get(normalizedName).push(jp);
        });
        console.timeEnd('[Worker] Indexing JSON data');
        console.log(\`[Worker] Indexed \${jsonIndexByPostcode.size} unique postcodes, \${jsonIndexByName.size} unique names.\`);
        // --- End Optimization ---

        const matchesArr = [];
        let initialNewArr = []; // Temporary array for items failing initial match
        const existingCodes = jsonArr.map(p => p.prac_code);

        // --- Optimization: Maintain max generated code number ---
        let maxGeneratedNum = 0;
        existingCodes.forEach(code => {
          if (typeof code === "string" && code.startsWith("N")) {
            const num = parseInt(code.slice(1), 10);
            if (!isNaN(num) && num > maxGeneratedNum) maxGeneratedNum = num;
          }
        });
        // --- End Optimization ---

        console.time('[Worker] Initial Comparison Pass');
        csvArr.forEach((csvP, index) => {
          try { // Add try...catch around loop iteration
            const csvName = csvP.Name || "";
            const csvAddr1 = csvP["Address 1"] || "";
            const csvPostcode = csvP.Postcode || "";
            const normalizedCsvPostcode = normalize(csvPostcode);

            let found = null;

            // --- Optimization: Look up potential matches by postcode ---
            const potentialMatches = jsonIndexByPostcode.get(normalizedCsvPostcode) || [];
            // --- End Optimization ---

            // --- Refined Matching Logic ---
            const csvAddr1HasNumber = /\\d/.test(csvAddr1); // Check if original address1 has a digit
            const normalizedCsvAddr1 = normalize(csvAddr1); // Normalize once

            for (const jp of potentialMatches) {
              const normalizedJsonAddr1 = normalize(jp.address1);
              const normalizedJsonPostcode = normalize(jp.postcode);
              let isMatch = false;

              if (csvAddr1HasNumber) {
                // Strict matching if address1 has a number
                isMatch = (normalizedJsonAddr1 === normalizedCsvAddr1 &&
                           normalizedJsonPostcode === normalizedCsvPostcode);
              } else {
                // Fuzzy matching if address1 has no number
                isMatch = (fuzzyMatch(jp.address1, csvAddr1, 2) && // Use original strings for fuzzy
                           fuzzyMatch(jp.postcode, csvPostcode, 1));
              }

              if (isMatch) {
                // Found a practice based on the matching criteria
                found = jp;

                // Check for any differences, including name (using the updated comparePractices)
                const diffs = comparePractices(found, csvP);
                if (diffs.length > 0) {
                  // If there are differences, add to matches for review
                  matchesArr.push({
                    jsonPractice: found,
                    csvPractice: csvP,
                    diffs,
                    approve: false
                  });
                }
                // else: Perfect match or only ignored differences - do nothing for now

                break; // Found the best match based on criteria, stop searching
              }
            }
            // --- End Refined Matching Logic ---

            // If no practice was found matching the criteria after checking all potentials
            if (!found) {
              // Don't generate code yet, just add to temporary list
              initialNewArr.push({ csvPractice: csvP, approve: false });
            }
          } catch(loopError) {
             console.error('[Worker] Error comparing CSV row index', index, ':', loopError);
             // Optionally post an error message back
             // self.postMessage({ action: "error", message: "Error comparing row " + index });
          }
        });
        console.timeEnd('[Worker] Initial Comparison Pass');

        // --- Post-processing: Categorize initialNewArr ---
        console.time('[Worker] Categorizing New/Potential/Possible/NameChange');
        const definiteNewArr = [];
        const potentialDuplicatesArr = [];
        const possibleNewArr = [];
        const nameChangeCandidatesArr = []; // <-- New array for name change candidates

        initialNewArr.forEach(entry => {
          const csvP = entry.csvPractice;
          const normalizedCsvPostcode = normalize(csvP.Postcode);
          let categorized = false;

          // Check if postcode exists in JSON index
          if (!jsonIndexByPostcode.has(normalizedCsvPostcode)) {
            // Bucket 1: Definite New (Postcode doesn't exist in JSON)
            maxGeneratedNum++;
            const newPracCode = "N" + String(maxGeneratedNum).padStart(5, "0");
            definiteNewArr.push({
              ...entry,
              generatedPracCode: newPracCode
            });
            categorized = true;
          } else {
            // Postcode exists, check for fuzzy matches
            const postcodeCandidates = jsonIndexByPostcode.get(normalizedCsvPostcode);
            let foundPotentialDuplicate = false;
            let foundNameChangeCandidate = false; // <-- Flag for name change
            let closestJsonMatchForPossible = null; // Store the closest JSON match for 'Possible New'

            for (const jp of postcodeCandidates) {
              const nameFuzzyMatch = fuzzyMatch(csvP.Name, jp.prac_name, 3);
              const address1FuzzyMatch = fuzzyMatch(csvP["Address 1"], jp.address1, 4);

              if (nameFuzzyMatch && address1FuzzyMatch) {
                // Bucket 2: Potential Duplicate (Postcode exists, fuzzy match found)
                console.log(\`[Worker] Potential Duplicate Found (Fuzzy): CSV '\${csvP.Name}' / '\${csvP["Address 1"]}' matches JSON '\${jp.prac_name}' / '\${jp.address1}' (Code: \${jp.prac_code})\`);
                potentialDuplicatesArr.push({
                  jsonPractice: jp,
                  csvPractice: csvP,
                  approve: false // Default to not approved
                });
                foundPotentialDuplicate = true;
                categorized = true;
                break; // Found a fuzzy match for this CSV entry
              } else if (address1FuzzyMatch && !nameFuzzyMatch && !foundNameChangeCandidate) {
                // Bucket 4: Name Change Candidate (Postcode exists, Address1 fuzzy matches, Name does NOT)
                console.log(\`[Worker] Name Change Candidate Found: CSV '\${csvP.Name}' vs JSON '\${jp.prac_name}' (Code: \${jp.prac_code}) - Address1 matches.\`);
                nameChangeCandidatesArr.push({
                    jsonPractice: jp,
                    csvPractice: csvP,
                    approve: false
                });
                foundNameChangeCandidate = true;
                categorized = true;
                // Don't break immediately, a potential duplicate might still exist further down?
                // Let's break for now for simplicity: assume the first address match without name match is the candidate.
                break;
              } else {
                // Store this candidate as a potential closest match if we end up in 'Possible New'
                // (For simplicity, just store the first one found with the same postcode)
                if (!closestJsonMatchForPossible) {
                   closestJsonMatchForPossible = {
                       prac_code: jp.prac_code,
                       prac_name: jp.prac_name,
                       address1: jp.address1, // Add address1 for context
                       postcode: jp.postcode // Add postcode for context
                   };
                }
              }
            }

            if (!foundPotentialDuplicate && !foundNameChangeCandidate) {
              // Bucket 3: Possible New (Postcode exists, but NO fuzzy match found)
              maxGeneratedNum++;
              const newPracCode = "N" + String(maxGeneratedNum).padStart(5, "0");
              possibleNewArr.push({
                 ...entry,
                 generatedPracCode: newPracCode,
                 closestJsonMatch: closestJsonMatchForPossible // Add the closest JSON match info
              });
               categorized = true;
            }
          }
           if (!categorized) {
               console.warn("[Worker] Entry was not categorized:", csvP); // Should not happen
           }
        });
        console.timeEnd('[Worker] Categorizing New/Potential/Possible/NameChange');
        // --- End Post-processing ---

        console.log('[Worker] Comparison finished:', {
          matchesCount: matchesArr.length,
          definiteNewCount: definiteNewArr.length,
          potentialDuplicatesCount: potentialDuplicatesArr.length,
          nameChangeCandidatesCount: nameChangeCandidatesArr.length, // <-- Log new count
          possibleNewCount: possibleNewArr.length
        });
        self.postMessage({
          action: "compareResult",
          matches: matchesArr,
          definiteNew: definiteNewArr, // Send definite new
          potentialDuplicates: potentialDuplicatesArr, // Send potential duplicates
          nameChangeCandidates: nameChangeCandidatesArr, // <-- Send new array
          possibleNew: possibleNewArr // Send possible new
        });
      }
    } catch (workerError) {
       console.error('[Worker] Uncaught Error:', workerError);
       self.postMessage({ action: "error", message: workerError.message || 'Unknown worker error' }); // Post error back
    }
  };
`;

// --- React Component ---
const STORAGE_KEY = "dataMergeReviewState_v2";

// Define normalize function in the component scope
function normalize(str) {
  // Ensure input is a string before processing
  return String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "").replace(/\s+/g, "").trim();
}

// Helper function to check if a value is considered empty (null, undefined, empty string, 'NA')
const isEmpty = (value) => {
    const strValue = String(value || "").trim();
    return strValue === "" || strValue.toLowerCase() === 'na';
};


const DataMergeTool = () => {
  const [matches, setMatches] = useState([]);
  // --- New States for Categories ---
  const [definiteNew, setDefiniteNew] = useState([]);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [nameChangeCandidates, setNameChangeCandidates] = useState([]); // <-- New state
  const [possibleNew, setPossibleNew] = useState([]);
  // --- End New States ---
  const [approved, setApproved] = useState({});
  const [step, setStep] = useState(1);
  const [workerReady, setWorkerReady] = useState(false);
  const workerRef = useRef(null);
  const [jsonData, setJsonData] = useState([]);
  const [workerError, setWorkerError] = useState(null); // Add state for worker errors
  const [isComparing, setIsComparing] = useState(false); // Add state for comparison loading
  const [downloadConfirmation, setDownloadConfirmation] = useState(''); // State for inline confirmation
  const [csvData, setCsvData] = useState([]); // Add state for CSV data
  const [loadingCsv, setLoadingCsv] = useState(false); // Add state for CSV loading
  const [error, setError] = useState(''); // Add state for general errors
  const [infoMessage, setInfoMessage] = useState(''); // Add state for info messages

  // Setup worker on mount
  useEffect(() => {
    const blob = new Blob([WORKER_SCRIPT], { type: "application/javascript" });
    const worker = new Worker(URL.createObjectURL(blob));
    workerRef.current = worker;
    worker.onmessage = (e) => {
      console.log('[Main] Received message from worker:', e.data); // Log all messages
      setWorkerError(null); // Clear previous errors

      if (e.data.action === "init") {
        setWorkerReady(true);
        console.log('[Main] Worker is ready.');
      }
      if (e.data.action === "compareResult") {
        console.log('[Main] Received compare results:', {
          matches: e.data.matches.length,
          definiteNew: e.data.definiteNew.length, // Log new count
          potentialDuplicates: e.data.potentialDuplicates.length,
          nameChangeCandidates: e.data.nameChangeCandidates.length, // <-- Log new count
          possibleNew: e.data.possibleNew.length
        });
        setMatches(e.data.matches);
        // --- Set New States ---
        setDefiniteNew(e.data.definiteNew);
        setPotentialDuplicates(e.data.potentialDuplicates);
        setNameChangeCandidates(e.data.nameChangeCandidates); // <-- Set new state
        setPossibleNew(e.data.possibleNew);
        // --- End Set New States ---
        setStep(4);
        setIsComparing(false); // Clear loading state on result
      }
      if (e.data.action === "error") { // Handle errors from worker
         console.error('[Main] Received error from worker:', e.data.message);
         setWorkerError(e.data.message);
         setIsComparing(false); // Clear loading state on error
      }
    };
    worker.postMessage({ action: "init" });
    return () => worker.terminate();
  }, []);

  // Restore minimal state from localStorage if available
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const state = JSON.parse(saved);
        // Restore approved state for all types
        setApproved(state.approved || {});
        setStep(state.step || 1);
        // Note: matches, newEntries, potentialDuplicates are NOT restored,
        // they are recalculated on compare.
      } catch {}
    }
  }, []);

  // Save only minimal state to localStorage
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ approved, step }) // Only save approved status and step
    );
  }, [approved, step]);

  // File upload handlers
  const handleJsonUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const data = JSON.parse(evt.target.result);
        const validData = Array.isArray(data) ? data : [];
        setJsonData(validData); // <-- Store original JSON data
        console.log(`[Main] Stored ${validData.length} records from JSON file in component state.`);
        workerRef.current.postMessage({ action: "storeJSON", data: validData });
        setStep(2);
        setDownloadConfirmation(''); // Clear confirmation on new upload
      } catch {
        alert("Invalid JSON file");
        setJsonData([]); // Clear state on error
      }
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = e => {
    const file = e.target.files[0];
    if (!file) return;

    // *** START: Reset state before processing new file ***
    setCsvData([]);
    setError('');
    setInfoMessage('');
    setLoadingCsv(true);
    // *** END: Reset state ***

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: results => {
        console.log("CSV Parsed:", results.data.length, "rows");
        setError(''); // Clear previous errors
        setInfoMessage(''); // Clear previous info messages

        // *** START: Deduplication Logic ***
        const latestEntries = {};
        let processedCount = 0;
        let invalidDateCount = 0;
        let dateWarningMessage = ''; // Store date warning message

        results.data.forEach(row => {
          // Define a unique key for deduplication based on address1 and postcode
          // Use the CSV header names directly if they differ from JSON keys
          const address1 = row['Address 1'] || row.address1 || '';
          const postcode = row['Postcode'] || row.postcode || '';
          const reportDateStr = row['Report publication date']; // Use exact header name

          if (!address1 || !postcode) {
            console.warn("Skipping row due to missing address1 or postcode:", row);
            return; // Skip rows without essential keys
          }

          const normalizedAddress1 = normalize(address1);
          const normalizedPostcode = normalize(postcode);
          const key = `${normalizedAddress1}|${normalizedPostcode}`;

          // Use new Date() directly for ISO 8601 format
          const currentDate = reportDateStr ? new Date(reportDateStr) : null;
          const isCurrentDateValid = currentDate && !isNaN(currentDate.getTime());

          if (!isCurrentDateValid) {
            invalidDateCount++;
            console.warn(`Invalid or missing date for row: ${reportDateStr}`, row);
            // Keep the first entry encountered if its date is invalid,
            // but allow it to be replaced by an entry with a valid date.
            if (!latestEntries[key]) {
              latestEntries[key] = { ...row, _parseError: 'Invalid Date' };
            }
            // Skip comparison logic if the current date is invalid
            return;
          }

          const existingEntry = latestEntries[key];

          if (!existingEntry) {
            // First entry for this address/postcode
            latestEntries[key] = row;
          } else {
            // Existing entry found, compare dates
            const storedDateStr = existingEntry['Report publication date'];
            const storedDate = storedDateStr ? new Date(storedDateStr) : null;
            const isStoredDateValid = storedDate && !isNaN(storedDate.getTime());

            // If the stored entry has an invalid date, replace it with the current valid one
            if (!isStoredDateValid) {
                 latestEntries[key] = row;
            }
            // If both dates are valid, compare them
            else if (currentDate.getTime() > storedDate.getTime()) {
              // Current entry is newer
              latestEntries[key] = row;
            }
            // If current date is older or equal, keep the existing entry
          }
          processedCount++;
        });

        const deduplicatedCsvData = Object.values(latestEntries);
        console.log("CSV Deduplicated:", deduplicatedCsvData.length, "rows remaining");
        if (invalidDateCount > 0) {
            console.warn(`Encountered ${invalidDateCount} rows with invalid or missing 'Report publication date'.`);
            // --- SET date warning message ---
            dateWarningMessage = `${invalidDateCount} rows had invalid/missing dates and might not have been correctly deduplicated.`;
        }
        // *** END: Deduplication Logic ***

        setCsvData(deduplicatedCsvData); // Set state with deduplicated data
        setLoadingCsv(false);
        // --- SET info message ---
        let successMessage = `${deduplicatedCsvData.length} unique practice entries loaded from CSV.`;
        setInfoMessage(dateWarningMessage ? `${successMessage} ${dateWarningMessage}` : successMessage);

        workerRef.current.postMessage({ action: "storeCSV", data: deduplicatedCsvData }); // Send deduplicated data
        setStep(3);
        setDownloadConfirmation(''); // Clear confirmation on new upload
      },
      error: (err) => {
        console.error("CSV Parsing Failed:", err);
        // --- SET error message ---
        setError(`CSV Parsing Failed: ${err.message}. Please ensure it's a valid CSV file.`);
        setInfoMessage(''); // Clear info message on error
        setLoadingCsv(false);
      }
    });
  };

  // Matching logic via worker
  const handleCompare = () => {
    if (!workerReady || isComparing) return;
    setIsComparing(true); // Set loading state
    setWorkerError(null); // Clear previous errors
    setDownloadConfirmation(''); // Clear confirmation on compare
    workerRef.current.postMessage({ action: "compare" });
  };

  // --- New: Refresh Comparison Handler ---
  const handleRefreshCompare = () => {
    if (!workerReady || isComparing) return;
    console.log('[Main] Refreshing comparison...');
    setIsComparing(true); // Set loading state
    setWorkerError(null); // Clear previous errors
    setDownloadConfirmation(''); // Clear confirmation on refresh
    // Clear review state before re-comparing
    setMatches([]);
    setDefiniteNew([]);
    setPotentialDuplicates([]);
    setNameChangeCandidates([]); // <-- Clear new state
    setPossibleNew([]);
    setApproved({});
    // Trigger comparison in worker (using existing stored data)
    workerRef.current.postMessage({ action: "compare" });
  };
  // --- End New Handler ---

  // --- Approve/reject handlers for EACH category ---
  const handleApprove = (category, index, approveStatus) => {
    const key = `${category}_${index}`;
    switch (category) {
      case 'match':
        setMatches(prev => prev.map((item, i) => (i === index ? { ...item, approve: approveStatus } : item)));
        break; // Uses true/false
      case 'potential':
        setPotentialDuplicates(prev => prev.map((item, i) => (i === index ? { ...item, approve: approveStatus } : item)));
        break; // Uses true/false
      case 'nameChange':
        setNameChangeCandidates(prev => prev.map((item, i) => (i === index ? { ...item, approve: approveStatus } : item)));
        break; // Uses true/false
      case 'possible':
        // Uses 'add', 'update', false
        setPossibleNew(prev => prev.map((item, i) => (i === index ? { ...item, approve: approveStatus } : item)));
        break;
      case 'definite':
        setDefiniteNew(prev => prev.map((item, i) => (i === index ? { ...item, approve: approveStatus } : item)));
        break; // Uses true/false
      default:
        console.error("Unknown category for approval:", category);
        return;
    }
    setApproved(prev => ({ ...prev, [key]: approveStatus }));
  };

  // --- Section-Specific Approve All Handlers ---
  const handleApproveAll = (category) => {
    const newApproved = { ...approved };
    let updatedItems;

    switch (category) {
      case 'match':
        updatedItems = matches.map((item, idx) => {
          newApproved[`match_${idx}`] = true; // Approve update
          return { ...item, approve: true };
        });
        setMatches(updatedItems);
        break;
      case 'potential':
        updatedItems = potentialDuplicates.map((item, idx) => {
          newApproved[`potential_${idx}`] = true; // Approve update
          return { ...item, approve: true };
        });
        setPotentialDuplicates(updatedItems);
        break;
      case 'nameChange':
        updatedItems = nameChangeCandidates.map((item, idx) => {
          newApproved[`nameChange_${idx}`] = true; // Approve update
          return { ...item, approve: true };
        });
        setNameChangeCandidates(updatedItems);
        break;
      case 'possible':
        updatedItems = possibleNew.map((item, idx) => {
          newApproved[`possible_${idx}`] = 'add'; // Approve ADD only
          return { ...item, approve: 'add' };
        });
        setPossibleNew(updatedItems);
        break;
      case 'definite':
        updatedItems = definiteNew.map((item, idx) => {
          newApproved[`definite_${idx}`] = true; // Approve add
          return { ...item, approve: true };
        });
        setDefiniteNew(updatedItems);
        break;
      default:
        console.error("Unknown category for approve all:", category);
        return;
    }
    setApproved(newApproved);
  };
  // --- End Approve All Handlers ---

  // --- Modified: Merge and Download ---
  const handleMergeAndDownload = () => {
    console.log('[Merge] Starting merge process...');
    setDownloadConfirmation(''); // Clear previous confirmation

    // Ensure jsonData is populated
    if (!jsonData || jsonData.length === 0) {
        console.error('[Merge Error] Original JSON data is not loaded in component state.');
        setWorkerError('Original JSON data is missing. Please re-upload the JSON file.');
        return;
    }
    console.log(`[Merge] Starting with ${jsonData.length} practices from initial JSON state.`);

    // Create a deep copy to avoid modifying the original state directly during processing
    let mergedArr = JSON.parse(JSON.stringify(jsonData));

    // Helper function to get value or fallback to existing if new is empty/null/undefined/whitespace/NA
    const getValueOrDefault = (newValue, oldValue) => {
      // Use the isEmpty helper function defined outside the component
      const isNewValueEffectivelyEmpty = isEmpty(newValue);
      return isNewValueEffectivelyEmpty ? oldValue : newValue;
    };

    let matchUpdates = 0;
    let potentialUpdates = 0;
    let nameChangeUpdates = 0; // <-- Count for name changes
    let possibleAdds = 0;
    let possibleUpdates = 0; // <-- Count for possible updates
    let definiteAdds = 0;

    // Process approved matches (updates)
    matches.forEach((m, idx) => {
      if (approved[`match_${idx}`]) {
        const practiceIndex = mergedArr.findIndex(p => p.prac_code === m.jsonPractice.prac_code);
        if (practiceIndex !== -1) {
          const p = mergedArr[practiceIndex];
          mergedArr[practiceIndex] = {
            ...p,
            prac_name: getValueOrDefault(m.csvPractice.Name, p.prac_name),
            address1: getValueOrDefault(m.csvPractice["Address 1"], p.address1),
            address2: getValueOrDefault(m.csvPractice["Address 2"], p.address2),
            address3: getValueOrDefault(m.csvPractice["Address 3"], p.address3),
            address4: getValueOrDefault(m.csvPractice["Town/City"], p.address4),
            postcode: getValueOrDefault(m.csvPractice.Postcode, p.postcode),
            type: getValueOrDefault(m.csvPractice.Type, p.type),
            con_name: getValueOrDefault(m.csvPractice["Constituency Name"], p.con_name)
          };
          matchUpdates++;
        } else {
          console.warn(`[Merge Warning - Match] Practice ${m.jsonPractice.prac_code} from 'matches' not found in mergedArr.`);
        }
      }
    });

    // Process approved potential duplicates (updates)
    potentialDuplicates.forEach((pd, idx) => {
      if (approved[`potential_${idx}`]) {
        const practiceIndex = mergedArr.findIndex(p => p.prac_code === pd.jsonPractice.prac_code);
        if (practiceIndex !== -1) {
          const p = mergedArr[practiceIndex];
          mergedArr[practiceIndex] = {
            ...p,
            prac_name: getValueOrDefault(pd.csvPractice.Name, p.prac_name),
            address1: getValueOrDefault(pd.csvPractice["Address 1"], p.address1),
            address2: getValueOrDefault(pd.csvPractice["Address 2"], p.address2),
            address3: getValueOrDefault(pd.csvPractice["Address 3"], p.address3),
            address4: getValueOrDefault(pd.csvPractice["Town/City"], p.address4),
            postcode: getValueOrDefault(pd.csvPractice.Postcode, p.postcode),
            type: getValueOrDefault(pd.csvPractice.Type, p.type),
            con_name: getValueOrDefault(pd.csvPractice["Constituency Name"], p.con_name)
          };
          potentialUpdates++;
        } else {
           console.warn(`[Merge Warning - Potential] Practice ${pd.jsonPractice.prac_code} from 'potentialDuplicates' not found in mergedArr.`);
        }
      }
    });

    // Process approved name change candidates (updates) <-- New section
    nameChangeCandidates.forEach((nc, idx) => {
      if (approved[`nameChange_${idx}`]) {
        const practiceIndex = mergedArr.findIndex(p => p.prac_code === nc.jsonPractice.prac_code);
        if (practiceIndex !== -1) {
          const p = mergedArr[practiceIndex];
          // Apply updates using getValueOrDefault, same as potential duplicates
          mergedArr[practiceIndex] = {
            ...p,
            prac_name: getValueOrDefault(nc.csvPractice.Name, p.prac_name),
            address1: getValueOrDefault(nc.csvPractice["Address 1"], p.address1),
            address2: getValueOrDefault(nc.csvPractice["Address 2"], p.address2),
            address3: getValueOrDefault(nc.csvPractice["Address 3"], p.address3),
            address4: getValueOrDefault(nc.csvPractice["Town/City"], p.address4),
            postcode: getValueOrDefault(nc.csvPractice.Postcode, p.postcode),
            type: getValueOrDefault(nc.csvPractice.Type, p.type),
            con_name: getValueOrDefault(nc.csvPractice["Constituency Name"], p.con_name)
            // Add other fields if necessary
          };
          nameChangeUpdates++;
        } else {
           console.warn(`[Merge Warning - Name Change] Practice ${nc.jsonPractice.prac_code} from 'nameChangeCandidates' not found in mergedArr.`);
        }
      }
    });

    // Helper function to create a new practice object from CSV
    const createNewPracticeObject = (csvPractice, generatedCode) => ({
        prac_code: generatedCode,
        prac_name: getValueOrDefault(csvPractice.Name, ""),
        address1: getValueOrDefault(csvPractice["Address 1"], ""),
        address2: getValueOrDefault(csvPractice["Address 2"], ""),
        address3: getValueOrDefault(csvPractice["Address 3"], ""),
        address4: getValueOrDefault(csvPractice["Town/City"], ""),
        postcode: getValueOrDefault(csvPractice.Postcode, ""),
        icb: getValueOrDefault(csvPractice.ICB, ""),
        type: getValueOrDefault(csvPractice.Type, ""),
        pcon: getValueOrDefault(csvPractice.PCON, ""),
        con_name: getValueOrDefault(csvPractice["Constituency Name"], "")
        // Add other default fields if needed
    });

    // Process approved possible new entries (additions OR updates)
    possibleNew.forEach((pn, idx) => {
      const approvalStatus = approved[`possible_${idx}`];
      const csvP = pn.csvPractice;

      if (approvalStatus === 'add') {
        // Add as new practice
        const newPractice = createNewPracticeObject(csvP, pn.generatedPracCode);
        mergedArr.push(newPractice);
        possibleAdds++;
      } else if (approvalStatus === 'update') {
        // Update the closest JSON match
        if (pn.closestJsonMatch && pn.closestJsonMatch.prac_code) {
          const practiceIndex = mergedArr.findIndex(p => p.prac_code === pn.closestJsonMatch.prac_code);
          if (practiceIndex !== -1) {
            const p = mergedArr[practiceIndex];
            // Apply updates using getValueOrDefault
            mergedArr[practiceIndex] = {
              ...p,
              prac_name: getValueOrDefault(csvP.Name, p.prac_name),
              address1: getValueOrDefault(csvP["Address 1"], p.address1),
              address2: getValueOrDefault(csvP["Address 2"], p.address2),
              address3: getValueOrDefault(csvP["Address 3"], p.address3),
              address4: getValueOrDefault(csvP["Town/City"], p.address4),
              postcode: getValueOrDefault(csvP.Postcode, p.postcode), // Usually same, but update just in case
              type: getValueOrDefault(csvP.Type, p.type),
              con_name: getValueOrDefault(csvP["Constituency Name"], p.con_name)
              // Add other fields if necessary
            };
            possibleUpdates++;
          } else {
            console.warn(`[Merge Warning - Possible Update] Practice ${pn.closestJsonMatch.prac_code} from 'possibleNew' not found in mergedArr.`);
          }
        } else {
           console.warn(`[Merge Warning - Possible Update] Missing closestJsonMatch or prac_code for 'possibleNew' index ${idx}. Cannot update.`);
        }
      }
      // If status is false or undefined, do nothing (reject)
    });

    // Process approved definite new entries (additions)
    definiteNew.forEach((dn, idx) => {
      if (approved[`definite_${idx}`]) {
         if (!mergedArr.some(p => p.prac_code === dn.generatedPracCode)) {
          mergedArr.push(createNewPracticeObject(dn.csvPractice, dn.generatedPracCode));
          definiteAdds++;
        } else {
          console.warn(`[Merge Warning - Definite New] ${dn.generatedPracCode} already exists?`);
        }
      }
    });

    console.log(`[Merge Summary] Final count: ${mergedArr.length}. Match Updates: ${matchUpdates}. Potential Updates: ${potentialUpdates}. Name Change Updates: ${nameChangeUpdates}. Possible Adds: ${possibleAdds}. Possible Updates: ${possibleUpdates}. Definite Adds: ${definiteAdds}.`);

    // Check if V03504 is present before download
    const finalCheck = mergedArr.find(p => p.prac_code === 'V03504');
    if (finalCheck) {
        console.log('[Merge Final Check] Practice V03504 IS present in the final merged array.');
    } else {
        console.error('[Merge Final Check] Practice V03504 IS MISSING from the final merged array!');
        // You could try to find it in the original jsonData again here for comparison
        const originalCheck = jsonData.find(p => p.prac_code === 'V03504');
        if (originalCheck) {
            console.error('[Merge Final Check] V03504 was present in the initial jsonData state.');
        } else {
            console.error('[Merge Final Check] V03504 was ALSO missing from the initial jsonData state used for merging!');
        }
    }


    // Download logic
    try {
        const blob = new Blob([JSON.stringify(mergedArr, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "merged_practices.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        console.log('[Merge] Download initiated.');
        // Set confirmation message
        setDownloadConfirmation(`Successfully generated merged_practices.json with ${mergedArr.length} practices.`);
    } catch (downloadError) {
        console.error('[Merge Error] Failed to generate download:', downloadError);
        setWorkerError('Failed to generate the download file. Check console for details.');
        setDownloadConfirmation(''); // Clear confirmation on error
    }
  };
  // --- End Modified ---

  // --- Modified: Clear Review State ---
  const handleClearReview = () => {
    localStorage.removeItem(STORAGE_KEY);
    setMatches([]);
    setDefiniteNew([]);
    setPotentialDuplicates([]);
    setNameChangeCandidates([]); // <-- Clear new state
    setPossibleNew([]);
    setApproved({});
    setJsonData([]); // Clear jsonData state as well
    setWorkerError(null); // Clear errors
    setStep(1);
    setDownloadConfirmation(''); // Clear confirmation
    if (workerRef.current) {
       console.log('[Main] Resetting worker and clearing stores.');
       workerRef.current.postMessage({ action: "init" }); // This will re-init and clear stores
    }
  };
  // --- End Modified ---

  // --- Modified: Calculate Summary Counts ---
  const totalMatchesFound = matches.length; // Matches with differences
  // Note: The current logic only puts matches *with differences* into the matches array.
  // If you wanted total potential matches (including perfect ones), the worker would need adjustment.
  const totalDefiniteNewFound = definiteNew.length;
  const totalPotentialDuplicatesFound = potentialDuplicates.length; // Add count
  const totalNameChangeCandidatesFound = nameChangeCandidates.length; // <-- Add count
  const totalPossibleNewFound = possibleNew.length;
  const totalItemsForReview = totalMatchesFound + totalDefiniteNewFound + totalPotentialDuplicatesFound + totalNameChangeCandidatesFound + totalPossibleNewFound; // <-- Update total
  // --- End Modified ---

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl"> {/* Increased max-width */}
      <h1 className="text-2xl font-bold mb-4 text-purple-400">Practice Data Merge Tool</h1>
      <ol className="mb-6 text-gray-300">
        <li>1. Upload your current practices JSON file.</li>
        <li>2. Upload the new data source CSV file.</li>
        <li>3. Compare, review, and approve changes or additions.</li>
        <li>4. Download the merged JSON file.</li>
      </ol>

      {/* Display Worker Error */}
      {workerError && (
        <div className="my-4 p-3 bg-red-900/30 border border-red-500/50 text-red-300 rounded">
          <p className="font-bold">An error occurred during processing:</p>
          <p className="text-sm">{workerError}</p>
          <p className="text-xs mt-2">Please check the console for more details and try clearing the review state.</p>
        </div>
      )}

      {/* --- ADD: Display Inline Error Message --- */}
      {error && (
        <div className="my-4 p-3 bg-red-900/30 border border-red-500/50 text-red-300 rounded">
          <p className="font-bold">Error:</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* --- ADD: Display Inline Info Message --- */}
      {infoMessage && (
        <div className="my-4 p-3 bg-blue-900/30 border border-blue-500/50 text-blue-300 rounded">
          <p className="text-sm">{infoMessage}</p>
        </div>
      )}

      {step === 4 && (
        <div className="mb-4 flex flex-col gap-3">
          {/* --- Modified: Summary Display --- */}
          <div className="flex flex-wrap gap-2 items-center">
             <span className="inline-block bg-purple-700 text-white px-3 py-1 rounded shadow text-sm">
               Reviewing {totalItemsForReview} item{totalItemsForReview !== 1 ? 's' : ''}
             </span>
             <span className="text-xs text-gray-400">
                (Matches: {totalMatchesFound}, Pot. Duplicates: {totalPotentialDuplicatesFound}, Name Changes: {totalNameChangeCandidatesFound}, Poss. New: {totalPossibleNewFound}, Def. New: {totalDefiniteNewFound}) {/* <-- Update summary text */}
             </span>
          </div>
          {/* --- End Modified --- */}
          <div className="flex gap-4 items-center">
             {/* --- New: Refresh Button --- */}
             <button
               className="text-xs text-blue-300 border-blue-500/50 hover:bg-blue-900/30 hover:text-blue-200"
               onClick={handleRefreshCompare}
               disabled={isComparing || !workerReady}
             >
               <svg className={`mr-2 h-3 w-3 ${isComparing ? 'animate-spin' : ''}`} viewBox="0 0 24 24">
                 <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
               </svg>
               {isComparing ? 'Refreshing...' : 'Refresh Comparison'}
             </button>
             {/* --- End New Button --- */}
             <button className="text-xs text-red-400 underline" onClick={handleClearReview}>
               Clear review state and start over
             </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="mb-6">
          <label className="block mb-2 text-white font-medium">Upload Current JSON:</label>
          <input type="file" accept=".json" onChange={handleJsonUpload} className="mb-4" />
        </div>
      )}

      {step === 2 && (
        <div className="mb-6">
          <label className="block mb-2 text-white font-medium">Upload New Data Source (CSV):</label>
          <input type="file" accept=".csv" onChange={handleCsvUpload} className="mb-4" disabled={loadingCsv} />
          {/* --- ADD: Loading indicator --- */}
          {loadingCsv && <p className="text-xs text-gray-400 mt-1">Processing CSV...</p>}
        </div>
      )}

      {step === 3 && (
        <div className="mb-6">
          <button
            className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleCompare}
            disabled={!workerReady || isComparing}
          >
            {isComparing ? (
              <>
                <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                </svg>
                Comparing...
              </>
            ) : (
              'Compare Data'
            )}
          </button>
          {!workerReady && <p className="text-xs text-gray-400 mt-1">Worker initializing...</p>}
        </div>
      )}

      {step === 4 && (
        <div>
          {/* --- Section 1: Matched Practices with Differences --- */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-lg text-white">
                Matched Practices with Differences ({totalMatchesFound})
              </h3>
              {matches.length > 0 && (
                <button
                  className="bg-green-700 hover:bg-green-800 text-white px-3 py-1 rounded text-xs shadow"
                  onClick={() => handleApproveAll('match')}
                  title="Approve all updates in this section"
                >
                  Approve All Matches
                </button>
              )}
            </div>
            {/* ... Table/Card rendering for matches (use handleApprove('match', idx, true/false)) ... */}
             {matches.length === 0 && <div className="text-gray-400">No matches with differences found.</div>}
             {/* Example Card Structure (replace with your table if preferred) */}
             {matches.map((m, idx) => (
               <div key={`match-${idx}`} className="mb-4 border border-purple-500/20 rounded p-3 bg-[#23233a]">
                 <div className="mb-2 font-semibold text-purple-200">{m.jsonPractice.prac_name} ({m.jsonPractice.prac_code})</div>
                 <div className="grid grid-cols-2 gap-2 text-xs text-gray-300 mb-2">
                   <div>
                     <div className="font-bold underline">JSON (existing)</div>
                     <div><b>Address 1:</b> {m.jsonPractice.address1}</div>
                     <div><b>Address 2:</b> {m.jsonPractice.address2}</div>
                     <div><b>Address 3:</b> {m.jsonPractice.address3}</div>
                     <div><b>Address 4:</b> {m.jsonPractice.address4}</div>
                     <div><b>Postcode:</b> {m.jsonPractice.postcode}</div>
                   </div>
                   <div>
                     <div className="font-bold underline">CSV (new)</div>
                     <div><b>Address 1:</b> {m.csvPractice["Address 1"]}</div>
                     <div><b>Address 2:</b> {m.csvPractice["Address 2"]}</div>
                     <div><b>Address 3:</b> {m.csvPractice["Address 3"]}</div>
                     <div><b>Address 4:</b> {m.csvPractice["Town/City"]}</div>
                     <div><b>Postcode:</b> {m.csvPractice.Postcode}</div>
                   </div>
                 </div>
                 <ul className="mb-2">
                   {m.diffs.map((d, i) => (
                     <li key={i} className="text-xs text-gray-300">
                       <span className="font-bold">{d.field}:</span>{" "}
                       <span className="line-through text-red-400">{d.oldValue || "(empty)"}</span>{" "}
                       <span className="text-green-400">{d.newValue || "(empty)"}</span>
                     </li>
                   ))}
                 </ul>
                 <div>
                   <button className={`mr-2 px-3 py-1 rounded text-xs ${m.approve ? "bg-green-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('match', idx, true)}> Approve Update </button>
                   <button className={`px-3 py-1 rounded text-xs ${!m.approve ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('match', idx, false)}> Reject </button>
                 </div>
               </div>
             ))}
          </div>

          {/* --- Section 2: Review Potential Duplicates (Postcode Exists, Fuzzy Match Found) --- */}
          <div className="mb-6">
             <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg text-yellow-300">
                  Review Potential Duplicates ({totalPotentialDuplicatesFound})
                </h3>
                {potentialDuplicates.length > 0 && (
                  <button
                    className="bg-yellow-600 hover:bg-yellow-700 text-black px-3 py-1 rounded text-xs shadow"
                    onClick={() => handleApproveAll('potential')}
                    title="Approve all potential duplicates as updates"
                  >
                    Approve All Duplicates
                  </button>
                )}
             </div>
            <p className="text-xs text-yellow-200 mb-3">CSV entries matching an existing practice's Postcode + fuzzy Name/Address 1. Approving updates the existing record.</p>
            {/* ... Table/Card rendering for potentialDuplicates (use handleApprove('potential', idx, true/false)) ... */}
             {potentialDuplicates.length === 0 && <div className="text-gray-400">No potential duplicates found.</div>}
             {potentialDuplicates.map((pd, idx) => {
               const { csvPractice: csvRow, jsonPractice: existingPractice } = pd;
               const keptValueStyle = "italic text-gray-400 bg-gray-800/20";
               return (
                 <div key={`potential-${idx}`} className="mb-4 border border-yellow-500/30 rounded p-3 bg-[#3a3a23]">
                    <div className="mb-2 font-semibold text-yellow-200">Potential Duplicate: {csvRow.Name}</div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-300 mb-2">
                      <div>
                        <div className="font-bold underline">JSON (existing match by Postcode + Fuzzy Name/Address)</div>
                        <div><b>Prac Code:</b> {existingPractice.prac_code}</div>
                        {/* Apply conditional styling for kept values */}
                        <div className={isEmpty(csvRow.Name) && !isEmpty(existingPractice.prac_name) ? keptValueStyle : ''}>
                          <b>Name:</b> {existingPractice.prac_name}
                        </div>
                        <div className={isEmpty(csvRow["Address 1"]) && !isEmpty(existingPractice.address1) ? keptValueStyle : ''}>
                          <b>Address 1:</b> {existingPractice.address1}
                        </div>
                        <div className={isEmpty(csvRow["Address 2"]) && !isEmpty(existingPractice.address2) ? keptValueStyle : ''}>
                          <b>Address 2:</b> {existingPractice.address2}
                        </div>
                        <div className={isEmpty(csvRow["Address 3"]) && !isEmpty(existingPractice.address3) ? keptValueStyle : ''}>
                          <b>Address 3:</b> {existingPractice.address3}
                        </div>
                        <div className={isEmpty(csvRow["Town/City"]) && !isEmpty(existingPractice.address4) ? keptValueStyle : ''}>
                          <b>Address 4:</b> {existingPractice.address4}
                        </div>
                        <div className={isEmpty(csvRow.Postcode) && !isEmpty(existingPractice.postcode) ? keptValueStyle : ''}>
                          <b>Postcode:</b> {existingPractice.postcode}
                        </div>
                         <div className={isEmpty(csvRow.Type) && !isEmpty(existingPractice.type) ? keptValueStyle : ''}>
                          <b>Type:</b> {existingPractice.type}
                        </div>
                         <div className={isEmpty(csvRow["Constituency Name"]) && !isEmpty(existingPractice.con_name) ? keptValueStyle : ''}>
                          <b>Constituency:</b> {existingPractice.con_name}
                        </div>
                      </div>
                      <div>
                        <div className="font-bold underline">CSV (new data)</div>
                        <div><b>Name:</b> {csvRow.Name}</div>
                        <div><b>Address 1:</b> {csvRow["Address 1"]}</div>
                        <div><b>Address 2:</b> {csvRow["Address 2"]}</div>
                        <div><b>Address 3:</b> {csvRow["Address 3"]}</div>
                        <div><b>Address 4 (Town/City):</b> {csvRow["Town/City"]}</div>
                        <div><b>Postcode:</b> {csvRow.Postcode}</div>
                        <div><b>Type:</b> {csvRow.Type}</div>
                        <div><b>Constituency:</b> {csvRow["Constituency Name"]}</div>
                      </div>
                    </div>
                     <p className="text-xs text-yellow-100 mb-2">Approving will update the existing JSON record ({existingPractice.prac_code}) with the CSV data (skipping empty CSV fields).</p>
                    <div>
                      <button className={`mr-2 px-3 py-1 rounded text-xs ${pd.approve ? "bg-yellow-600 text-black" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('potential', idx, true)} title={`Update ${existingPractice.prac_code}`}> Approve as Update </button>
                      <button className={`px-3 py-1 rounded text-xs ${!pd.approve ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('potential', idx, false)} title="Ignore this CSV entry, keep existing JSON entry as is"> Reject (Ignore CSV) </button>
                    </div>
                 </div>
               );
             })}
          </div>

          {/* --- Section NEW: Review Potential Name Changes --- */}
          <div className="mb-6">
             <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg text-orange-300"> {/* Use a different color, e.g., orange */}
                  Review Potential Name Changes ({totalNameChangeCandidatesFound})
                </h3>
                {nameChangeCandidates.length > 0 && (
                  <button
                    className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-xs shadow" // Adjusted color
                    onClick={() => handleApproveAll('nameChange')}
                    title="Approve all potential name changes as updates"
                  >
                    Approve All Name Changes
                  </button>
                )}
             </div>
            <p className="text-xs text-orange-200 mb-3">CSV entries share a postcode and similar Address 1 with an existing practice, but the Name is different. Approving updates the existing record.</p>
             {nameChangeCandidates.length === 0 && <div className="text-gray-400">No potential name changes found.</div>}
             {nameChangeCandidates.map((nc, idx) => {
               const { csvPractice: csvRow, jsonPractice: existingPractice } = nc;
               const keptValueStyle = "italic text-gray-400 bg-gray-800/20";
               return (
                 <div key={`nameChange-${idx}`} className="mb-4 border border-orange-500/30 rounded p-3 bg-[#3a2d23]"> {/* Adjusted color */}
                    <div className="mb-2 font-semibold text-orange-200">Potential Name Change: {csvRow.Name} (CSV) vs {existingPractice.prac_name} (JSON)</div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-300 mb-2">
                      <div>
                        <div className="font-bold underline">JSON (existing match by Postcode + Fuzzy Address)</div>
                        <div><b>Prac Code:</b> {existingPractice.prac_code}</div>
                        {/* Highlight the name difference */}
                        <div className="font-bold text-red-400">
                          <b>Name:</b> {existingPractice.prac_name}
                        </div>
                        <div className={isEmpty(csvRow["Address 1"]) && !isEmpty(existingPractice.address1) ? keptValueStyle : ''}>
                          <b>Address 1:</b> {existingPractice.address1}
                        </div>
                        {/* ... (rest of JSON fields with keptValueStyle logic) ... */}
                         <div className={isEmpty(csvRow["Address 2"]) && !isEmpty(existingPractice.address2) ? keptValueStyle : ''}>
                          <b>Address 2:</b> {existingPractice.address2}
                        </div>
                        <div className={isEmpty(csvRow["Address 3"]) && !isEmpty(existingPractice.address3) ? keptValueStyle : ''}>
                          <b>Address 3:</b> {existingPractice.address3}
                        </div>
                        <div className={isEmpty(csvRow["Town/City"]) && !isEmpty(existingPractice.address4) ? keptValueStyle : ''}>
                          <b>Address 4:</b> {existingPractice.address4}
                        </div>
                        <div className={isEmpty(csvRow.Postcode) && !isEmpty(existingPractice.postcode) ? keptValueStyle : ''}>
                          <b>Postcode:</b> {existingPractice.postcode}
                        </div>
                         <div className={isEmpty(csvRow.Type) && !isEmpty(existingPractice.type) ? keptValueStyle : ''}>
                          <b>Type:</b> {existingPractice.type}
                        </div>
                         <div className={isEmpty(csvRow["Constituency Name"]) && !isEmpty(existingPractice.con_name) ? keptValueStyle : ''}>
                          <b>Constituency:</b> {existingPractice.con_name}
                        </div>
                      </div>
                      <div>
                        <div className="font-bold underline">CSV (new data)</div>
                        {/* Highlight the new name */}
                        <div className="font-bold text-green-400"><b>Name:</b> {csvRow.Name}</div>
                        <div><b>Address 1:</b> {csvRow["Address 1"]}</div>
                        {/* ... (rest of CSV fields) ... */}
                        <div><b>Address 2:</b> {csvRow["Address 2"]}</div>
                        <div><b>Address 3:</b> {csvRow["Address 3"]}</div>
                        <div><b>Address 4 (Town/City):</b> {csvRow["Town/City"]}</div>
                        <div><b>Postcode:</b> {csvRow.Postcode}</div>
                        <div><b>Type:</b> {csvRow.Type}</div>
                        <div><b>Constituency:</b> {csvRow["Constituency Name"]}</div>
                      </div>
                    </div>
                     <p className="text-xs text-orange-100 mb-2">Approving will update the existing JSON record ({existingPractice.prac_code}) with the CSV data (skipping empty CSV fields).</p>
                    <div>
                      <button className={`mr-2 px-3 py-1 rounded text-xs ${nc.approve ? "bg-orange-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('nameChange', idx, true)} title={`Update ${existingPractice.prac_code}`}> Approve Name Change </button>
                      <button className={`px-3 py-1 rounded text-xs ${!nc.approve ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('nameChange', idx, false)} title="Ignore this CSV entry, keep existing JSON entry as is"> Reject (Ignore CSV) </button>
                    </div>
                 </div>
               );
             })}
          </div>
          {/* --- End Section NEW --- */}


          {/* --- Section 3: Review Possible New (Postcode Match, No Fuzzy Match) --- */}
          <div className="mb-6">
             <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg text-blue-300">
                  Review Possible New (Postcode Match) ({totalPossibleNewFound})
                </h3>
                {possibleNew.length > 0 && (
                  <button
                    className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-1 rounded text-xs shadow"
                    onClick={() => handleApproveAll('possible')}
                    title="Approve adding all these as new practices"
                  >
                    Approve All as New
                  </button>
                )}
             </div>
            {/* --- Updated Description --- */}
            <p className="text-xs text-blue-200 mb-3">CSV entries share a postcode with an existing practice, but Name/Address 1 are different. Review carefully. You can add the CSV entry as a new practice, update the existing practice shown, or reject.</p>
            {/* --- End Updated Description --- */}
             {possibleNew.length === 0 && <div className="text-gray-400">No possible new practices found in this category.</div>}
             {possibleNew.map((pn, idx) => {
                const { csvPractice: csvRow, closestJsonMatch: existingPractice } = pn;
                const keptValueStyle = "italic text-gray-400 bg-gray-800/20";
                const mapsSearchTerm = `dental+${encodeURIComponent(csvRow.Postcode || '')}`;
                const mapsUrl = `https://www.google.co.uk/maps/search/${mapsSearchTerm}`;
                const isAdd = pn.approve === 'add';
                const isUpdate = pn.approve === 'update';
                const isReject = !isAdd && !isUpdate; // Default or explicitly false

                return (
                 <div key={`possible-${idx}`} className="mb-4 border border-blue-500/30 rounded p-3 bg-[#1e2d2d]">
                   <div className="mb-2 font-semibold text-blue-200">Possible New: {csvRow.Name} (CSV)</div>
                   {/* --- Display Side-by-Side --- */}
                   <div className="grid grid-cols-2 gap-2 text-xs text-gray-300 mb-2">
                     {existingPractice ? (
                       <div>
                         <div className="font-bold underline">JSON (Closest Match by Postcode)</div>
                         <div><b>Prac Code:</b> {existingPractice.prac_code}</div>
                         {/* Apply conditional styling for kept values if updating */}
                         <div className={isUpdate && isEmpty(csvRow.Name) && !isEmpty(existingPractice.prac_name) ? keptValueStyle : ''}>
                           <b>Name:</b> {existingPractice.prac_name}
                         </div>
                         <div className={isUpdate && isEmpty(csvRow["Address 1"]) && !isEmpty(existingPractice.address1) ? keptValueStyle : ''}>
                           <b>Address 1:</b> {existingPractice.address1}
                         </div>
                         <div className={isUpdate && isEmpty(csvRow["Address 2"]) && !isEmpty(existingPractice.address2) ? keptValueStyle : ''}>
                           <b>Address 2:</b> {existingPractice.address2}
                         </div>
                         <div className={isUpdate && isEmpty(csvRow["Address 3"]) && !isEmpty(existingPractice.address3) ? keptValueStyle : ''}>
                           <b>Address 3:</b> {existingPractice.address3}
                         </div>
                         <div className={isUpdate && isEmpty(csvRow["Town/City"]) && !isEmpty(existingPractice.address4) ? keptValueStyle : ''}>
                           <b>Address 4:</b> {existingPractice.address4}
                         </div>
                         <div className={isUpdate && isEmpty(csvRow.Postcode) && !isEmpty(existingPractice.postcode) ? keptValueStyle : ''}>
                           <b>Postcode:</b> {existingPractice.postcode}
                         </div>
                          <div className={isUpdate && isEmpty(csvRow.Type) && !isEmpty(existingPractice.type) ? keptValueStyle : ''}>
                           <b>Type:</b> {existingPractice.type}
                         </div>
                          <div className={isUpdate && isEmpty(csvRow["Constituency Name"]) && !isEmpty(existingPractice.con_name) ? keptValueStyle : ''}>
                           <b>Constituency:</b> {existingPractice.con_name}
                         </div>
                       </div>
                     ) : (
                       <div className="text-gray-400 italic">No close JSON match found (should not happen in this category).</div>
                     )}
                     <div>
                       <div className="font-bold underline">CSV (New Data)</div>
                       <div><b>Name:</b> {csvRow.Name}</div>
                       <div><b>Address 1:</b> {csvRow["Address 1"]}</div>
                       <div><b>Address 2:</b> {csvRow["Address 2"]}</div>
                       <div><b>Address 3:</b> {csvRow["Address 3"]}</div>
                       <div><b>Address 4 (Town/City):</b> {csvRow["Town/City"]}</div>
                       <div><b>Postcode:</b> {csvRow.Postcode}</div>
                       <div><b>Type:</b> {csvRow.Type}</div>
                       <div><b>Constituency:</b> {csvRow["Constituency Name"]}</div>
                       <div className="mt-1">
                         <span className="font-bold">Generated prac_code (if added):</span> <span className="text-blue-400">{pn.generatedPracCode}</span>
                       </div>
                     </div>
                   </div>
                   {/* --- End Display Side-by-Side --- */}

                   <div className="mt-3 flex items-center flex-wrap gap-2"> {/* Use flex-wrap */}
                     {/* --- Updated Buttons --- */}
                     <button
                       className={`px-3 py-1 rounded text-xs ${isAdd ? "bg-blue-600 text-white" : "bg-gray-700 text-gray-300"}`}
                       onClick={() => handleApprove('possible', idx, 'add')}
                       title={`Add ${csvRow.Name} as a new practice with code ${pn.generatedPracCode}`}
                     >
                       Approve Add
                     </button>
                     {existingPractice && ( // Only show update if there's a match to update
                       <button
                         className={`px-3 py-1 rounded text-xs ${isUpdate ? "bg-yellow-600 text-black" : "bg-gray-700 text-gray-300"}`}
                         onClick={() => handleApprove('possible', idx, 'update')}
                         title={`Update existing practice ${existingPractice.prac_code} (${existingPractice.prac_name}) with CSV data`}
                       >
                         Approve Update
                       </button>
                     )}
                     <button
                       className={`px-3 py-1 rounded text-xs ${isReject ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`}
                       onClick={() => handleApprove('possible', idx, false)}
                       title="Ignore this CSV entry"
                     >
                       Reject
                     </button>
                     {/* --- End Updated Buttons --- */}
                     {/* Google Maps Button */}
                     <a
                       href={mapsUrl}
                       target="_blank"
                       rel="noopener noreferrer"
                       className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-100 bg-blue-800 hover:bg-blue-900"
                       title={`Search Google Maps for 'dental ${csvRow.Postcode}'`}
                     >
                       <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
                       Maps
                     </a>
                   </div>
                 </div>
                );
             })}
          </div>

          {/* --- Section 4: Definite New Practices (New Postcode) --- */}
          <div className="mb-6">
             <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg text-green-300"> {/* Changed color */}
                  Definite New Practices (New Postcode) ({totalDefiniteNewFound})
                </h3>
                {definiteNew.length > 0 && (
                  <button
                    className="bg-green-700 hover:bg-green-800 text-white px-3 py-1 rounded text-xs shadow"
                    onClick={() => handleApproveAll('definite')}
                    title="Approve adding all new practices"
                  >
                    Approve All New
                  </button>
                )}
             </div>
             <p className="text-xs text-green-200 mb-3">CSV entries with postcodes not found in the existing JSON data. Approving adds them as new.</p>
            {/* ... Table/Card rendering for definiteNew (use handleApprove('definite', idx, true/false)) ... */}
             {definiteNew.length === 0 && <div className="text-gray-400">No new practices found with completely new postcodes.</div>}
             {definiteNew.map((dn, idx) => (
               <div key={`definite-${idx}`} className="mb-4 border border-green-500/20 rounded p-3 bg-[#1e2d1e]">
                 <div className="mb-2 font-semibold text-green-300">{dn.csvPractice.Name}</div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Address 1:</span> {dn.csvPractice["Address 1"]}
                 </div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Address 2:</span> {dn.csvPractice["Address 2"]}
                 </div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Address 3:</span> {dn.csvPractice["Address 3"]}
                 </div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Address 4 (Town/City):</span> {dn.csvPractice["Town/City"]}
                 </div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Postcode:</span> {dn.csvPractice.Postcode}
                 </div>
                 <div className="text-xs text-gray-300 mb-1">
                   <span className="font-bold">Generated prac_code:</span> <span className="text-green-400">{dn.generatedPracCode}</span>
                 </div>
                 <div className="mt-3 flex items-center gap-2">
                   <button className={`mr-2 px-3 py-1 rounded text-xs ${dn.approve ? "bg-green-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('definite', idx, true)}> Approve Add </button>
                   <button className={`px-3 py-1 rounded text-xs ${!dn.approve ? "bg-red-600 text-white" : "bg-gray-700 text-gray-300"}`} onClick={() => handleApprove('definite', idx, false)}> Reject </button>
                 </div>
               </div>
             ))}
          </div>

          {/* --- Download Button --- */}
          <div className="mt-6 flex flex-col items-start gap-3">
            <button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded disabled:opacity-50"
              onClick={handleMergeAndDownload}
              disabled={isComparing} // Disable if comparison is running
            >
              Download Merged JSON
            </button>
            {/* Inline Confirmation Message */}
            {downloadConfirmation && (
              <p className="text-sm text-green-400">{downloadConfirmation}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataMergeTool;
